import React, { Fragment, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  BellIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  ArrowUturnLeftIcon,
  XCircleIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import type { ActionId } from '@/features/trades/hooks';
// Direct module import (not the barrel) — mirrors PartialRecallModal: the barrel
// pulls the whole hooks tree, which mocking-sensitive unit tests can't satisfy.
import {
  SET_CLOSE_DATE_TOOLTIP,
  TERMINATE_TOOLTIP,
  CANCEL_REJECTED_TOOLTIP,
} from '@/features/trades/hooks/trade-actions';

const { Button: MenuButton, Items: MenuItems, Item: MenuItem } = Menu;

interface TradeActionButtonsProps {
  /** The valid action set (from the shared `getValidActions` predicate). */
  validActions: Set<ActionId>;
  onGiveNotice: () => void;
  onSetSettlementDate: () => void;
  onSettle: () => void;
  onSettleCash: () => void;
  onSettleSecurities: () => void;
  onTerminate: () => void;
  // LR-388: partial recall/return — the bar is stateless, so the parent resolves
  // the side-aware label (lend → Recall, borrow → Return) and passes it in.
  onPartialRecall: () => void;
  // LR-478: amend the booked quantity (pending only).
  onAmend: () => void;
  // LR-544: manually fire the Trade Date action ("Open now", pending only).
  onProcessTradeDate: () => void;
  /**
   * LR-544: when set, the "Open now" button renders DISABLED with this string as
   * its tooltip — used for a pending trade whose trade date is still in the
   * future (force-open is out of scope; it opens automatically on Trade Date).
   */
  openNowDisabledReason?: string;
  // LR-528: re-rate an open trade (active / backdated matured).
  onReRate: () => void;
  partialRecallLabel?: string;
  /** "Settle Collateral" on non-cash collateral, where the leg is securities (`collateralLegName`). */
  settleCashLabel?: string;
  /**
   * Condensed mode: collapses all non-primary, non-terminate actions into the ···
   * overflow so the bar stays a tight primary + ··· + Terminate cluster. Currently
   * unused by the app (the single-trade header is always full-width); retained and
   * unit-tested as the tight-space layout for a future condensed/responsive surface.
   */
  collapsed?: boolean;
  /**
   * LR-478 (single-trade nav redesign): navigation entries (View in MTM / Mark
   * history) folded into the overflow ··· menu so they no longer compete with the
   * lifecycle verbs. Page-injected, so they stay reachable even on terminal
   * trades (empty validActions), where the lifecycle bar collapses to a note.
   */
  navItems?: TradeNavItem[];
}

type ActionMeta = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  /** Optional hover hint; LR-576 (feedback п.70) disambiguates close-date vs Terminate. */
  title?: string;
};

/**
 * LR-478 (single-trade nav redesign): a page-injected navigation entry rendered
 * in the SAME overflow menu as the lifecycle actions (below a divider). Owned by
 * the page so the toolbar stays agnostic of MTM / counterparty concerns.
 */
export type TradeNavItem = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
};

const NEUTRAL_BTN =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors';
const PRIMARY_BTN =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors';
const DESTRUCTIVE_BTN =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors';
// LR-478: navigation entries (View in MTM / Mark history) — outline/blue so they
// read as "go elsewhere", distinct from the filled lifecycle verbs. Surfaced
// inline in the full header; folded into the ··· menu only on the pinned bar.
const NAV_BTN =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors';
// Reused focus-ring token set (matches TradeTabNavigation) so keyboard focus is
// always visible as the user arrows across the toolbar.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent focus-visible:ring-offset-2 focus-visible:rounded-lg';

// Lifecycle secondary order. partial_recall is ABSENT here — it carries a
// side-aware label and is appended after the secondaries (see restLifecycle).
// terminate/retry are handled separately (trailing destructive button / not
// surfaced on the detail bar).
const SECONDARY_ORDER: ActionId[] = [
  'give_notice',
  'set_settlement_date',
  'settle',
  'settle_cash',
  'settle_securities',
  'amend',
  're_rate',
];

/**
 * Variant A+ trade-action toolbar (LR-478). An ARIA `role="toolbar"` with a
 * single contextual PRIMARY action. In the FULL header every other valid action
 * is surfaced inline (lifecycle secondaries + partial_recall + page nav) —
 * nothing hidden, since the full-width row has the room (LR-478 recompose). A
 * `collapsed` mode (currently unused by the app; retained for a tight surface)
 * instead packs them all into an overflow `···` menu. Trailing divided red
 * TERMINATE. Roving tabindex (Arrow/Home/End) makes the cluster a single tab stop.
 *
 * Stateless: each control fires the matching callback; modal state + mutation
 * wiring live ONE LEVEL up (TradeDetailPage), so the toolbar can be mounted in
 * multiple places (TradeHeader / StickySummary) without tearing down a dialog.
 * Validity gating is the shared `getValidActions` predicate — render and submit
 * agree by construction (ACTN-02); the page re-checks `canPerform` on submit.
 *
 * The toolbar surfaces no "Recall" control (that is a Trades-page row action) and
 * knows nothing about MTM — the status-action-matrix guard renders it WITHOUT
 * navItems and asserts no MTM button. Re-Rate (LR-528) IS surfaced here as a
 * lifecycle secondary on active / matured trades. Navigation (View in MTM / Mark
 * history) is OPT-IN via the page-injected `navItems`, folded into the same ···
 * overflow (LR-478 single-trade nav redesign) so it never competes with the
 * lifecycle verbs.
 */
export function TradeActionButtons({
  validActions,
  onGiveNotice,
  onSetSettlementDate,
  onSettle,
  onSettleCash,
  onSettleSecurities,
  onTerminate,
  onPartialRecall,
  onAmend,
  onProcessTradeDate,
  openNowDisabledReason,
  onReRate,
  partialRecallLabel = 'Partial Recall',
  settleCashLabel = 'Settle Cash',
  collapsed = false,
  navItems,
}: TradeActionButtonsProps) {
  // Roving-tabindex active control. Hooks must run before any early return.
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const navList = navItems ?? [];

  // Empty action set (closed / cancelled) → R9 terminal note (D-05 / Phase 15).
  // rejected is NOT empty here ({retry, terminate}) — the bar renders its
  // Cancel button instead. Plain <p>, not a button — status-action-matrix.test
  // asserts no action buttons render in the empty case. LR-478: when the page
  // injects nav (e.g. Mark history, which outlives close), fall through so the
  // note renders alongside a nav-only ··· menu instead of swallowing it.
  if (validActions.size === 0 && navList.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        Position closed — no further actions
      </p>
    );
  }

  const META: Partial<Record<ActionId, ActionMeta>> = {
    give_notice: { label: 'Give Notice', icon: BellIcon, onClick: onGiveNotice },
    // LR-576 (feedback п.70): named after the LR-406 blotter column — the action
    // stamps the CLOSE settlement date; the previous label read as open-side.
    set_settlement_date: {
      label: 'Set Close Date',
      icon: CalendarDaysIcon,
      onClick: onSetSettlementDate,
      title: SET_CLOSE_DATE_TOOLTIP,
    },
    settle: { label: 'Settle', icon: CheckCircleIcon, onClick: onSettle },
    settle_cash: { label: settleCashLabel, icon: BanknotesIcon, onClick: onSettleCash },
    settle_securities: { label: 'Settle Securities', icon: DocumentCheckIcon, onClick: onSettleSecurities },
    partial_recall: { label: partialRecallLabel, icon: ArrowUturnLeftIcon, onClick: onPartialRecall },
    amend: { label: 'Amend', icon: PencilSquareIcon, onClick: onAmend },
    process_trade_date: { label: 'Open now', icon: PlayCircleIcon, onClick: onProcessTradeDate },
    re_rate: { label: 'Re-Rate', icon: AdjustmentsHorizontalIcon, onClick: onReRate },
  };

  // rejected ({retry, terminate}) → terminate is the date-less Cancel (LR-478).
  // retry stays a Trades-page row-panel action; the detail bar surfaces Cancel only.
  const isRejected = validActions.has('retry');
  const terminateLabel = isRejected ? 'Cancel' : 'Terminate';
  const hasTerminate = validActions.has('terminate');

  // Primary = the one dominant next step: Settle when settling is due,
  // else Give Notice for an evergreen un-noticed trade, else Open now for a
  // pending trade (LR-544), else none.
  const primaryId: ActionId | null = validActions.has('settle')
    ? 'settle'
    : validActions.has('give_notice')
      ? 'give_notice'
      : validActions.has('process_trade_date')
        ? 'process_trade_date'
        : null;

  // LR-478 full-width recompose: the EXPANDED header has room, so surface every
  // non-primary action inline (nothing hidden under ···). The COLLAPSED pinned
  // bar stays tight — primary + ··· (all secondaries + recall + nav) + Terminate
  // — so the 4-stat sticky row can never wrap.
  const secondaryEligible = SECONDARY_ORDER.filter((id) => id !== primaryId && validActions.has(id));
  const restLifecycle: ActionId[] = [
    ...secondaryEligible,
    ...(validActions.has('partial_recall') ? (['partial_recall'] as ActionId[]) : []),
  ];
  const inlineLifecycle = collapsed ? [] : restLifecycle;
  const inlineNav = collapsed ? [] : navList;
  const overflowLifecycle = collapsed ? restLifecycle : [];
  const overflowNav = collapsed ? navList : [];
  const hasOverflow = overflowLifecycle.length > 0 || overflowNav.length > 0;

  // Ordered control keys = the roving-tabindex sequence (primary → inline
  // lifecycle → inline nav → overflow menu button → terminate). Index drives
  // which control is the single tab stop (tabIndex 0); the rest are -1 and
  // reachable only via the arrows.
  const controlKeys: string[] = [];
  if (primaryId) controlKeys.push('primary');
  inlineLifecycle.forEach((id) => controlKeys.push(`sec:${id}`));
  inlineNav.forEach((n) => controlKeys.push(`nav:${n.key}`));
  if (hasOverflow) controlKeys.push('overflow');
  if (hasTerminate) controlKeys.push('terminate');
  const activeKeyIdx = Math.min(activeIdx, Math.max(0, controlKeys.length - 1));
  const rovingProps = (key: string) => {
    const idx = controlKeys.indexOf(key);
    return {
      'data-toolbar-item': true,
      tabIndex: idx === activeKeyIdx ? 0 : -1,
      onFocus: () => setActiveIdx(idx),
    } as const;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    // LR-478 review r1 (#1/#9): only drive roving focus when a toolbar control is
    // the active element. When the ··· overflow menu is OPEN, headlessui owns focus
    // on a menu item (which is NOT a [data-toolbar-item]) and does not handle
    // ArrowLeft/ArrowRight — those would otherwise bubble up to this handler, find
    // no active toolbar item (current === -1), and yank focus out to the primary /
    // terminate button, dismissing the menu. Bail so the open menu keeps focus.
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !active.matches('[data-toolbar-item]')) return;
    const root = toolbarRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-toolbar-item]'));
    if (items.length === 0) return;
    const current = items.findIndex((el) => el === document.activeElement);
    let next: number;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'ArrowRight') next = current < 0 ? 0 : (current + 1) % items.length;
    else next = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length;
    e.preventDefault();
    items[next]?.focus();
  };

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label="Trade actions"
      className="flex items-center gap-2"
      onKeyDown={handleKeyDown}
    >
      {validActions.size === 0 && (
        // LR-478: terminal trade with nav still available — show the closed-note
        // inline, alongside the nav-only ··· menu (e.g. Mark history).
        <p className="mr-1 text-sm italic text-gray-500">Position closed — no further actions</p>
      )}
      {primaryId && META[primaryId] && (
        (() => {
          const m = META[primaryId]!;
          const Icon = m.icon;
          // LR-544: "Open now" is disabled (with a tooltip) for a pending trade
          // whose trade date is still in the future — it opens automatically.
          const disabled = primaryId === 'process_trade_date' && !!openNowDisabledReason;
          // LR-544 r2 (kvi P1): use aria-disabled, NOT the `disabled` attr. A real
          // `disabled` primary drops out of tab order, and since it holds the
          // roving tab stop (activeIdx 0) the WHOLE toolbar becomes unreachable by
          // keyboard on the canonical future-TD screen (Amend / nav / ··· stranded
          // at tabIndex -1). aria-disabled keeps it focusable + announces the state;
          // the onClick guard blocks activation (WCAG 2.1.1).
          return (
            <button
              {...rovingProps('primary')}
              onClick={disabled ? undefined : m.onClick}
              aria-disabled={disabled || undefined}
              title={disabled ? openNowDisabledReason : m.title}
              className={`${PRIMARY_BTN} ${FOCUS_RING}${disabled ? ' opacity-50 cursor-not-allowed hover:bg-blue-600' : ''}`}
            >
              <Icon className="h-4 w-4" />
              {m.label}
            </button>
          );
        })()
      )}

      {inlineLifecycle.map((id) => {
        const m = META[id]!;
        const Icon = m.icon;
        return (
          <button key={id} {...rovingProps(`sec:${id}`)} onClick={m.onClick} title={m.title} className={`${NEUTRAL_BTN} ${FOCUS_RING}`}>
            <Icon className="h-4 w-4" />
            {m.label}
          </button>
        );
      })}

      {/* LR-478: navigation surfaced inline in the full header (outline/blue),
          separated from the lifecycle verbs by a hairline when both are present. */}
      {inlineNav.length > 0 && (primaryId || inlineLifecycle.length > 0) && (
        <span className="mx-1 h-6 w-px self-center bg-gray-200" aria-hidden="true" />
      )}
      {inlineNav.map((n) => {
        const Icon = n.icon;
        return (
          <button key={n.key} {...rovingProps(`nav:${n.key}`)} onClick={n.onClick} className={`${NAV_BTN} ${FOCUS_RING}`}>
            <Icon className="h-4 w-4" />
            {n.label}
          </button>
        );
      })}

      {hasOverflow && (
        <Menu as="div" className="relative">
          <MenuButton
            {...rovingProps('overflow')}
            aria-label="More actions"
            title="More actions"
            className={`inline-flex items-center justify-center rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 transition-colors ${FOCUS_RING}`}
          >
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </MenuButton>
          {/* motion-safe: gates the scale/opacity pop for prefers-reduced-motion
              users (CLAUDE.md a11y constraint). Without the transition utility the
              from/to states apply instantly — no visible motion (r7). */}
          <Transition
            as={Fragment}
            enter="motion-safe:transition motion-safe:ease-out motion-safe:duration-100"
            enterFrom="transform motion-safe:opacity-0 motion-safe:scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="motion-safe:transition motion-safe:ease-in motion-safe:duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform motion-safe:opacity-0 motion-safe:scale-95"
          >
            {/* z-40: above the sticky header (z-20), below modals (z-100+). */}
            <MenuItems className="absolute right-0 z-40 mt-2 w-52 origin-top-right rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
              {overflowLifecycle.map((id) => {
                const m = META[id]!;
                const Icon = m.icon;
                return (
                  <MenuItem key={id}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={m.onClick}
                        title={m.title}
                        className={`${active ? 'bg-gray-50' : ''} flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700`}
                      >
                        <Icon className="h-4 w-4 text-gray-400" />
                        {m.label}
                      </button>
                    )}
                  </MenuItem>
                );
              })}
              {/* LR-478: divider between lifecycle overflow and page-injected
                  navigation, only when both groups are present. */}
              {overflowLifecycle.length > 0 && overflowNav.length > 0 && (
                <div className="my-1 h-px bg-gray-100" aria-hidden="true" />
              )}
              {overflowNav.map((n) => {
                const Icon = n.icon;
                return (
                  <MenuItem key={n.key}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={n.onClick}
                        className={`${active ? 'bg-gray-50' : ''} flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700`}
                      >
                        <Icon className="h-4 w-4 text-gray-400" />
                        {n.label}
                      </button>
                    )}
                  </MenuItem>
                );
              })}
            </MenuItems>
          </Transition>
        </Menu>
      )}

      {hasTerminate && (
        <>
          <span className="mx-1 h-6 w-px self-center bg-gray-200" aria-hidden="true" />
          <button
            {...rovingProps('terminate')}
            onClick={onTerminate}
            // LR-576 (feedback п.70): disambiguate from Set Close Date on hover.
            title={isRejected ? CANCEL_REJECTED_TOOLTIP : TERMINATE_TOOLTIP}
            className={`${DESTRUCTIVE_BTN} ${FOCUS_RING}`}
          >
            <XCircleIcon className="h-4 w-4" />
            {terminateLabel}
          </button>
        </>
      )}
    </div>
  );
}
